/**
 * What each back-office role may see and do (DEV-67, docs/plans/roles-matrix.md).
 *
 * The console's copy of the server's permissions matrix
 * (apps/api/src/config/staff-capabilities.ts). The server enforces it; this
 * decides what the console shows, so a section or button a role cannot use is
 * not offered at all. Keep both in step: a capability added here without the
 * server side is a button that answers 403.
 */

import type { AdminRole } from '../api/auth.api.js';

export type Capability =
  /** Row 1 — customers, accounts, KYC status and risk (masked below MLRO). */
  | 'customers.view'
  /** Row 2 — Reveal, identity documents. */
  | 'pii.reveal'
  /** Row 3 — Accounts → Open account. */
  | 'accounts.open'
  /** Row 4 — freeze/suspend/close/unfreeze, freeze/cancel card. */
  | 'customer.blocks'
  /** Row 5 — change email, temporary password. */
  | 'customer.access'
  /** Rows 4/5 — the customer Actions page holds both. */
  | 'customer.actions'
  /** Rows 1/6/7 — the transaction feed and payment queues, read. */
  | 'transactions.view'
  /** Rows 6/7 — the full review drawers (finance gets the read-only summary). */
  | 'payments.workspace'
  /** Row 6 — release / refuse / attribute, manual incoming entry. */
  | 'payments.review'
  /** Row 7 — send a reviewed manual withdrawal, manual outgoing entry. */
  | 'payments.send'
  /** Row 8 — KYC and onboarding decisions, onboarding (the KYC Queue was folded into it, DEV-160). */
  | 'kyc.decide'
  /** Row 9 — AML alerts, read. */
  | 'aml.view'
  /** Row 10 — work and close AML alerts, STR. */
  | 'aml.work'
  /** Rows 11/12 — fees and commissions, read. */
  | 'fees.view'
  /** Rows 11/12 — fees and commissions, change. */
  | 'fees.edit'
  /** Row 13 — comments, read. */
  | 'comments.view'
  /** Row 13 — comments, write. */
  | 'comments.write'
  /** Row 14 — customer statements. */
  | 'statements'
  /** Row 15 — Reports → Commissions and Transactions. */
  | 'reports.financial'
  /** Row 16 — Reports → Regulatory. */
  | 'reports.regulatory'
  /** Row 17 — reconciliation, read. */
  | 'reconciliation.view'
  /** Row 17 — reconciliation, resolve breaks. */
  | 'reconciliation.work'
  /** Row 18 — Settings: onboarding policy, monitoring, risk scoring. */
  | 'settings.compliance'
  /** Row 19 — Settings: organisation (DEV-138), integrations, admin users, alerts. */
  | 'settings.admin'
  /** Row 20 — View as customer. */
  | 'impersonate'
  /** Settings hub: every role sees at least the fee schedules (row 11). */
  | 'settings.view'
  /** Not in the matrix — the client-money (safeguarding) position; closed to support. */
  | 'client-money.view'
  /** Not in the matrix — dashboard and notification bell. */
  | 'console';

const A: AdminRole = 'admin';
const M: AdminRole = 'compliance';
const C: AdminRole = 'client_manager';
const F: AdminRole = 'finance';
const S: AdminRole = 'support';
const ALL: readonly AdminRole[] = [A, M, C, F, S];

export const CAPABILITIES: Readonly<Record<Capability, readonly AdminRole[]>> = {
  'customers.view': ALL,
  'pii.reveal': [A, M],
  'accounts.open': [A, M],
  'customer.blocks': [A, M, S],
  'customer.access': [A, S],
  'customer.actions': [A, M, S],
  'transactions.view': [A, M, C, F],
  'payments.workspace': [A, M, C],
  'payments.review': [A, M],
  'payments.send': [A, C],
  'kyc.decide': [A, M],
  'aml.view': [A, M, C, F],
  'aml.work': [A, M],
  'fees.view': ALL,
  'fees.edit': [A, C],
  'comments.view': [A, M, C, F],
  'comments.write': [A, M, C],
  statements: [A, M, C, F],
  'reports.financial': [A, M, C, F],
  'reports.regulatory': [A, M],
  'reconciliation.view': [A, M, F],
  'reconciliation.work': [A, M],
  'settings.compliance': [A, M],
  'settings.admin': [A],
  impersonate: [A, M, C, S],
  'settings.view': ALL,
  'client-money.view': [A, M, C, F],
  console: [A, M, C, S],
};

/** True when `role` holds `capability`. An unknown or missing role holds nothing. */
export function can(role: string | null | undefined, capability: Capability): boolean {
  return role !== null && role !== undefined && (CAPABILITIES[capability] as readonly string[]).includes(role);
}

/** The label each role carries in the console. The code value of MLRO stays `compliance`. */
export const ROLE_LABELS: Readonly<Record<AdminRole, string>> = {
  admin: 'Admin',
  compliance: 'AML / MLRO',
  client_manager: 'Client manager',
  finance: 'Finance',
  support: 'Support',
};

/** The label for a session's role; a role this console does not know reads as no access. */
export function roleLabel(role: string | null | undefined): string {
  return role !== null && role !== undefined && role in ROLE_LABELS ? ROLE_LABELS[role as AdminRole] : 'No access';
}

/** What each role is for, shown when an administrator assigns it. */
export const ROLE_DESCRIPTIONS: Readonly<Record<AdminRole, string>> = {
  admin: 'Full access, including integrations, admin users and alert routing.',
  compliance: 'Opens and closes accounts, releases and refuses payments, decides KYC and works AML alerts. Sees fees and commissions but cannot change them.',
  client_manager: 'Sets fee schedules and per-payment commissions, writes comments, and sends manual withdrawals that passed review. Does not open accounts, release payments or change KYC.',
  finance: 'Read-only access to customers, payments, fees, reconciliation and reports, with exports and statements for P&L. Approves and changes nothing; MFA is required.',
  support: 'Masked customer view, account and card blocks, and customer sign-in help (email change, temporary password).',
};

/** Where a role lands after signing in: its first useful screen. */
export function homePath(role: string | null | undefined): string {
  return can(role, 'console') ? '/dashboard' : can(role, 'transactions.view') ? '/transactions' : '/access-denied';
}
